import { useState, useMemo } from 'react';
import { Plus, Filter, FileText, RefreshCw, Pencil, Receipt, Paperclip, Copy } from 'lucide-react';
import { Button, IconButton } from '../components/ui/Button';
import { StatusPill } from '../components/ui/StatusPill';
import { PageHeader } from '../components/PageHeader';
import { RowActions } from '../components/RowActions';
import { STATUS_LIST } from '../lib/constants';
import { formatBRL, formatDateBR, monthYearBR } from '../lib/formatters';

export function HomeView({
  solicitacoes, empresas, tomadores, loading,
  onNew, onEdit, onView, onDelete, onAttach, onPdf, onStatusChange, onSendNF, onCopy,
}) {
  const [filterAno, setFilterAno] = useState('todos');
  const [filterMes, setFilterMes] = useState('todos');
  const [filterEmp, setFilterEmp] = useState('todas');
  const [filterStatus, setFilterStatus] = useState('todos');
  // Tomador: 'todos' ou id de tomador
  const [filterTomador, setFilterTomador] = useState('todos');
  // PI: string livre (busca substring case-insensitive)
  const [filterPI, setFilterPI] = useState('');

  const anos = useMemo(() => {
    const set = new Set(solicitacoes.map(s => (s.data_solicitacao || '').slice(0, 4)).filter(Boolean));
    return ['todos', ...Array.from(set).sort().reverse()];
  }, [solicitacoes]);

  const meses = ['todos', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const mesNome = (m) => ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][Number(m)];

  const filtered = useMemo(() => solicitacoes.filter(s => {
    const [y, m] = (s.data_solicitacao || '').split('-');
    if (filterAno !== 'todos' && y !== filterAno) return false;
    if (filterMes !== 'todos' && m !== filterMes) return false;
    if (filterEmp !== 'todas' && s.empresa_id !== filterEmp) return false;
    if (filterStatus !== 'todos' && s.status !== filterStatus) return false;
    if (filterTomador !== 'todos' && s.tomador_id !== filterTomador) return false;
    if (filterPI.trim() !== '') {
      const q = filterPI.trim().toLowerCase();
      if (!(s.pi || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [solicitacoes, filterAno, filterMes, filterEmp, filterStatus, filterTomador, filterPI]);

  const stats = useMemo(() => {
    // Cancelada nunca entra em nenhum card
    const validas = filtered.filter(s => s.status !== 'Cancelada');
    const total = validas.reduce((a, s) => a + (Number(s.valor) || 0), 0);
    const recebido = validas
      .filter(s => s.status === 'Recebido')
      .reduce((a, s) => a + (Number(s.valor) || 0), 0);
    const emAberto = total - recebido; // Solicitada + Submetida + Atrasado
    return { total, recebido, emAberto, count: validas.length };
  }, [filtered]);

  const AnexoCell = ({ url, Icon, title }) => {
    if (!url) {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, color: 'var(--muted-2)', fontSize: 14,
        }} title={`${title}: não anexado`}>—</span>
      );
    }
    return (
      <a
        href={url} target="_blank" rel="noreferrer" title={title}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 8,
          color: 'var(--primary)', textDecoration: 'none',
          transition: 'background .15s',
        }}
        onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-2)'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon size={14} strokeWidth={2.2} />
      </a>
    );
  };

  const StatCard = ({ label, value, sub, accent }) => (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
      <div className="font-money" style={{
        fontSize: 26, fontWeight: 700, marginTop: 6, lineHeight: 1,
        color: accent || 'var(--ink)',
      }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="fade-in">
      <PageHeader
        title="Solicitações de NF"
        subtitle="Acompanhe todas as solicitações enviadas pelo Comercial à Cobrança"
        actions={<Button icon={Plus} onClick={onNew}>Nova solicitação</Button>}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18,
      }}>
        <StatCard
          label="Valor total"
          value={formatBRL(stats.total)}
          accent="var(--primary)"
        />
        <StatCard
          label="Valor recebido"
          value={formatBRL(stats.recebido)}
          sub="apenas status Recebido"
        />
        <StatCard
          label="Valor em aberto"
          value={formatBRL(stats.emAberto)}
          sub="Solicitada · Submetida · Atrasado"
          accent="var(--warning)"
        />
        <StatCard
          label="Quantidade de notas"
          value={stats.count}
        />
      </div>

      <div className="card" style={{ marginBottom: 14, padding: '14px 16px',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)',
          fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
          <Filter size={13} /> Filtros
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Ano</label>
          <select className="select" value={filterAno} onChange={e => setFilterAno(e.target.value)}
            style={{ width: 110, height: 36 }}>
            {anos.map(a => <option key={a} value={a}>{a === 'todos' ? 'Todos' : a}</option>)}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Mês</label>
          <select className="select" value={filterMes} onChange={e => setFilterMes(e.target.value)}
            style={{ width: 140, height: 36 }}>
            {meses.map(m => <option key={m} value={m}>{m === 'todos' ? 'Todos' : mesNome(m)}</option>)}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Empresa</label>
          <select className="select" value={filterEmp} onChange={e => setFilterEmp(e.target.value)}
            style={{ width: 200, height: 36 }}>
            <option value="todas">Todas</option>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.nome_fantasia}</option>)}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Status</label>
          <select className="select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ width: 160, height: 36 }}>
            <option value="todos">Todos</option>
            {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>Tomador</label>
          <select className="select" value={filterTomador} onChange={e => setFilterTomador(e.target.value)}
            style={{ width: 220, height: 36 }}>
            <option value="todos">Todos</option>
            {[...(tomadores || [])]
              .sort((a, b) => (a.nome_fantasia || a.razao_social || '').localeCompare(b.nome_fantasia || b.razao_social || ''))
              .map(t => (
                <option key={t.id} value={t.id}>{t.nome_fantasia || t.razao_social}</option>
              ))}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>PI</label>
          <input
            className="input"
            value={filterPI}
            onChange={e => setFilterPI(e.target.value)}
            placeholder="Buscar PI…"
            style={{ width: 160, height: 36 }}
          />
        </div>
        {(filterAno !== 'todos' || filterMes !== 'todos' || filterEmp !== 'todas' || filterStatus !== 'todos' || filterTomador !== 'todos' || filterPI.trim() !== '') && (
          <Button size="sm" variant="ghost" icon={RefreshCw}
            onClick={() => { setFilterAno('todos'); setFilterMes('todos'); setFilterEmp('todas'); setFilterStatus('todos'); setFilterTomador('todos'); setFilterPI(''); }}>
            Limpar
          </Button>
        )}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '64px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            Carregando solicitações…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '64px 20px', textAlign: 'center', color: 'var(--muted)' }}>
            <FileText size={28} style={{ color: 'var(--muted-2)', marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Nenhuma solicitação no filtro</div>
            <div style={{ fontSize: 13 }}>Ajuste os filtros ou crie uma nova solicitação.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['ID', 'PI', 'Empresa', 'Tomador', 'Solicitação', 'Competência', 'Valor', 'Solicitante', 'Nº Nota', 'Status', ''].map(h => (
                    <th key={h} style={{
                      padding: '10px 10px', textAlign: 'left', fontWeight: 600,
                      fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em',
                      color: 'var(--muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const emp = empresas.find(e => e.id === s.empresa_id);
                  const tom = tomadores.find(t => t.id === s.tomador_id);
                  const isCancelada = s.status === 'Cancelada';
                  return (
                    <tr key={s.id} style={{
                      borderBottom: '1px solid var(--border)',
                      opacity: isCancelada ? 0.45 : 1,
                      transition: 'opacity .15s, background .15s',
                    }}
                      onMouseOver={e => {
                        e.currentTarget.style.background = 'var(--surface-2)';
                        if (isCancelada) e.currentTarget.style.opacity = 0.85;
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'transparent';
                        if (isCancelada) e.currentTarget.style.opacity = 0.45;
                      }}>
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span className="font-mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                            {s.codigo_unico}
                          </span>
                          {s.serie_total > 1 && (
                            <span
                              title={
                                s.serie_tipo === 'parcelamento'
                                  ? `Parcela ${s.serie_posicao} de ${s.serie_total}`
                                  : `Recorrência ${s.serie_posicao} de ${s.serie_total}`
                              }
                              style={{
                                fontSize: 9.5, fontWeight: 700,
                                padding: '1px 5px', borderRadius: 999,
                                background: 'var(--primary-soft)',
                                color: 'var(--primary)',
                                border: '1px solid var(--primary-line)',
                                lineHeight: 1.3,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {s.serie_posicao}/{s.serie_total}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <span className="font-mono" style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>
                          {s.pi}
                        </span>
                      </td>
                      <td style={{ padding: '10px 10px', maxWidth: 180 }}>
                        {/* Empresas do exterior nao tem nome_fantasia — cai pra
                            razao_social pra nao mostrar '—'. Truncamento c/
                            tooltip pra nao quebrar em 3 linhas largas. */}
                        <div
                          title={emp?.nome_fantasia || emp?.razao_social || '—'}
                          style={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {emp?.nome_fantasia || emp?.razao_social || '—'}
                        </div>
                      </td>
                      <td style={{ padding: '10px 10px', maxWidth: 200 }}>
                        <div
                          title={tom?.nome_fantasia || tom?.razao_social || '—'}
                          style={{
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {tom?.nome_fantasia || tom?.razao_social || '—'}
                        </div>
                      </td>
                      <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                        {formatDateBR(s.data_solicitacao)}
                      </td>
                      <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                        {monthYearBR(s.data_prestacao_servico)}
                      </td>
                      <td className="font-money" style={{ padding: '10px 10px', fontWeight: 600,
                        whiteSpace: 'nowrap' }}>
                        {formatBRL(s.valor)}
                      </td>
                      <td style={{ padding: '10px 10px', color: 'var(--ink-2)' }}>{s.solicitante}</td>
                      <td style={{ padding: '10px 10px' }}>
                        <span className="font-mono" style={{
                          fontSize: 11.5, color: s.numero_nota ? 'var(--ink)' : 'var(--muted-2)',
                        }}>{s.numero_nota || '—'}</span>
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <StatusPill status={s.status} />
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <AnexoCell url={s.anexo_nota_url}          Icon={Receipt}   title="Anexo da nota fiscal emitida" />
                          <AnexoCell url={s.anexo_intermediario_url} Icon={Paperclip} title="Documento auxiliar" />
                          <IconButton icon={Copy}     title="Duplicar (cria novo já preenchido)" onClick={() => onCopy(s)} />
                          <IconButton icon={Pencil}   title="Editar"          onClick={() => onEdit(s)} />
                          <IconButton icon={FileText} title="Visualizar PDF"  onClick={() => onPdf(s)} />
                          <RowActions
                            onView={() => onView(s)}
                            onEdit={() => onEdit(s)}
                            onPdf={() => onPdf(s)}
                            onAttach={() => onAttach(s)}
                            onSendNF={() => onSendNF(s)}
                            hasAnexoNota={!!s.anexo_nota_url}
                            onDelete={() => onDelete(s)}
                            onStatusChange={(st) => onStatusChange(s, st)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
